import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ComingSoonBadge from '@/features/home-v2/components/ui/atoms/ComingSoonBadge';
import PortalTile from '@/features/home-v2/components/ui/molecules/PortalTile';
import {
  PORTAL_ICON_SIZE,
  PORTAL_MENU_ITEMS,
} from '@/features/home-v2/constants/portals';
import type { PortalMenuItem } from '@/features/home-v2/types/portals';
import { useChangeApp } from '@/shared/hooks/useChangeApp';
import './PortalsGrid.css';

export interface PortalsGridProps {
  /** Called after a portal tile triggered a navigation (used to close the popover) */
  onNavigate?: () => void;
  /** Custom className */
  className?: string;
}

/**
 * PortalsGrid
 *
 * Apps launcher grid of the unified header (Figma `1235:9011` — "Menu Grid
 * Portais"): the "Produtos" title plus one tile per portal. Selecting a tile
 * performs the IAM change-app flow towards the **home** page of the target
 * portal, so the user lands authenticated on the other product.
 *
 * The grid is the popover content of the header "Abrir aplicações" button —
 * `Header` owns the trigger and the popover, this organism owns the catalog and
 * the navigation.
 *
 * ## Navigation flow
 *
 * ```mermaid
 * flowchart TD
 *     A[Tile activated] --> B{Portal has url?}
 *     B -->|no| C[Inert tile — "Disponível em breve"]
 *     B -->|yes| D[onNavigate → close popover]
 *     D --> E{platform}
 *     E -->|pdt| F[useChangeApp → VITE_PDT_CLIENT_ID → PDT_LINKS.HOME]
 *     E -->|portalId| G[useChangeApp → VITE_PORTAL_ID_CLIENT_ID → PORTAL_ID_LINKS.HOME]
 * ```
 *
 * @example
 * ```tsx
 * <Header
 *   showAppsIcon
 *   appsMenuContent={(close) => <PortalsGrid onNavigate={close} />}
 * />
 * ```
 *
 * @since 1.0.0
 * @category Organisms
 */
export const PortalsGrid: React.FC<PortalsGridProps> = ({
  onNavigate,
  className = '',
}) => {
  const { t } = useTranslation();
  const { mutate: navigateTo } = useChangeApp();

  const handleSelect = useCallback(
    (portal: PortalMenuItem) => {
      if (!portal.url) return;

      onNavigate?.();
      navigateTo({
        url: portal.url,
        targetClientId:
          portal.platform === 'portalId'
            ? import.meta.env.VITE_PORTAL_ID_CLIENT_ID
            : import.meta.env.VITE_PDT_CLIENT_ID,
      });
    },
    [navigateTo, onNavigate]
  );

  return (
    <div
      className={`portals-grid ${className}`.trim()}
      data-testid="portals-grid"
      data-node-id="1235:9011"
    >
      <p className="portals-grid__title">{t('Products')}</p>
      <div
        className="portals-grid__items"
        role="group"
        aria-label={t('Products')}
      >
        {PORTAL_MENU_ITEMS.map((portal) => {
          const { Icon } = portal;
          const unavailable = !portal.url;

          return (
            <PortalTile
              key={portal.key}
              label={t(portal.labelKey)}
              disabled={unavailable}
              badge={unavailable ? <ComingSoonBadge /> : undefined}
              data-testid={`portal-tile-${portal.key}`}
              icon={<Icon width={PORTAL_ICON_SIZE} height={PORTAL_ICON_SIZE} />}
              onSelect={() => handleSelect(portal)}
            />
          );
        })}
      </div>
    </div>
  );
};

export default PortalsGrid;
